import React, { useState } from 'react';
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon className="w-4 h-4 text-emerald-500" />;
    case 'error':
      return <XCircleIcon className="w-4 h-4 text-red-500" />;
    case 'warning':
      return <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />;
    default:
      return <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />;
  }
};

const formatDuration = (ms) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const DataViewer = ({ data, label }) => {
  const [collapsed, setCollapsed] = useState(true);

  if (!data || data.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      >
        {collapsed ? <ChevronRightIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
        {label} ({data.length} {data.length === 1 ? 'item' : 'items'})
      </button>
      {!collapsed && (
        <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300 overflow-x-auto max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

const NodeResultRow = ({ node }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left transition-colors"
      >
        <StatusIcon status={node.status} />
        <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
          {node.name}
        </span>
        <span className="text-xs text-gray-400 tabular-nums">
          {formatDuration(node.duration)}
        </span>
        {expanded
          ? <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
          : <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-3 pl-11">
          {node.error && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-700 dark:text-red-400 mb-2">
              {node.error.message}
              {node.error.description && (
                <div className="mt-1 text-red-500 dark:text-red-500">{node.error.description}</div>
              )}
            </div>
          )}
          <DataViewer data={node.input} label="Input" />
          <DataViewer data={node.output} label="Output" />
          {!node.error && (!node.input || node.input.length === 0) && (!node.output || node.output.length === 0) && (
            <div className="text-xs text-gray-400 italic">No data</div>
          )}
        </div>
      )}
    </div>
  );
};

const ExecutionPanel = ({ execution, running, error, onClose }) => {
  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-xl z-20 flex flex-col" style={{ height: '320px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            Test Execution
          </span>

          {running && (
            <span className="inline-flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
              <div className="w-3 h-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              Running...
            </span>
          )}

          {!running && execution && (
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
              execution.status === 'success'
                ? 'text-emerald-600 dark:text-emerald-400'
                : execution.status === 'error'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-amber-600 dark:text-amber-400'
            }`}>
              <StatusIcon status={execution.status} />
              {execution.status === 'success' ? 'Completed' : execution.status === 'error' ? 'Failed' : 'Warning'}
              {execution.duration > 0 && (
                <span className="text-gray-400 font-normal ml-1">
                  in {formatDuration(execution.duration)}
                </span>
              )}
            </span>
          )}

          {!running && error && (
            <span className="text-xs text-red-500">{error}</span>
          )}
        </div>

        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Node results */}
      <div className="flex-1 overflow-y-auto">
        {!running && error ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
            <XCircleIcon className="w-8 h-8 text-red-400" />
            <p className="text-sm font-medium text-red-500">Test execution failed</p>
            <p className="text-xs text-gray-400 text-center max-w-md">{error}</p>
          </div>
        ) : execution?.nodes?.length > 0 ? (
          execution.nodes.map((node) => (
            <NodeResultRow key={node.name} node={node} />
          ))
        ) : running ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">
            Waiting for results...
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">
            No execution data yet
          </div>
        )}
      </div>

      {/* Footer summary */}
      {execution && !running && (
        <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-100 dark:border-gray-700/50 text-xs text-gray-400 flex-shrink-0">
          <span>{execution.stepsExecuted || 0} steps executed</span>
          {execution.error && (
            <span className="text-red-400">
              Failed at: {execution.error.node}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutionPanel;
